import type { ReactNode } from "react";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";

import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { formatMoneyBR } from "helpers/formatMoney";
import { IModalidade } from "interfaces/eventos";
import {
  ICreateSolicitacaoOrcamentoAdminPayload,
  IServicoOrcamento,
} from "interfaces/orcamento";
import { listAllModalidades } from "services/modalidade";
import { listarServicosOrcamentoAdmin } from "services/orcamentoServicos";
import { criarSolicitacaoOrcamentoAdmin } from "services/orcamentoSolicitacoes";
import {
  fieldSx,
  modalHeaderSx,
  modalPaperSx,
  ORCAMENTO_ACCENT,
  sectionCardSx,
} from "./orcamento-solicitacoes.theme";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (id: number, codigo: string) => void;
};

type FormState = {
  nomeOrganizador: string;
  email: string;
  telefone: string;
  cidade: string;
  dataEvento: string;
  horaLargada: string;
  numeroAtletas: string;
  modalidadeId: number | null;
  modalidadeTexto: string;
  distanciaProva: string;
  largadaChegadaMesmoPonto: boolean;
  observacoes: string;
  obsInterna: string;
};

const EMPTY_FORM: FormState = {
  nomeOrganizador: "",
  email: "",
  telefone: "",
  cidade: "",
  dataEvento: "",
  horaLargada: "",
  numeroAtletas: "1",
  modalidadeId: null,
  modalidadeTexto: "",
  distanciaProva: "",
  largadaChegadaMesmoPonto: false,
  observacoes: "",
  obsInterna: "",
};

function numbersOnly(value: string) {
  return value.replace(/\D/g, "");
}

function SectionTitle({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" mb={2}>
      <Box sx={{ color: ORCAMENTO_ACCENT, display: "flex" }}>{icon}</Box>
      <MDTypography variant="button" fontWeight="bold">
        {title}
      </MDTypography>
    </Stack>
  );
}

export default function NovaSolicitacaoOrcamentoModal({
  open,
  onClose,
  onCreated,
}: Props) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [servicos, setServicos] = useState<IServicoOrcamento[]>([]);
  const [modalidades, setModalidades] = useState<IModalidade[]>([]);
  const [selectedServicos, setSelectedServicos] = useState<
    Record<number, number>
  >({});
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadMeta = useCallback(async () => {
    setLoadingMeta(true);
    setError(null);
    try {
      const [listaServicos, listaModalidades] = await Promise.all([
        listarServicosOrcamentoAdmin(),
        listAllModalidades(),
      ]);
      setServicos(listaServicos.filter((s) => s.ativo));
      setModalidades(listaModalidades.filter((m) => m.id != null));
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : "Falha ao carregar serviços e modalidades."
      );
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setSelectedServicos({});
    setError(null);
    loadMeta();
  }, [open, loadMeta]);

  const modalidadeOptions = useMemo(
    () =>
      modalidades.map((m) => ({
        id: Number(m.id),
        label: m.tipo,
      })),
    [modalidades]
  );

  const selectedModalidade =
    modalidadeOptions.find((m) => m.id === form.modalidadeId) ?? null;

  const patchForm = (patch: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const toggleServico = (servico: IServicoOrcamento, checked: boolean) => {
    setSelectedServicos((prev) => {
      const next = { ...prev };
      if (checked) {
        next[servico.id] = servico.permiteQuantidade ? prev[servico.id] || 1 : 1;
      } else {
        delete next[servico.id];
      }
      return next;
    });
  };

  const setQuantidade = (servicoId: number, quantidade: number) => {
    setSelectedServicos((prev) => ({
      ...prev,
      [servicoId]: Math.max(1, Math.floor(quantidade) || 1),
    }));
  };

  const validate = (): string | null => {
    if (form.nomeOrganizador.trim().length < 2) {
      return "Informe o nome do organizador.";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return "Informe um e-mail válido.";
    }
    if (numbersOnly(form.telefone).length < 8) {
      return "Informe um telefone válido.";
    }
    if (form.cidade.trim().length < 2) {
      return "Informe a cidade do evento.";
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.dataEvento)) {
      return "Informe a data do evento.";
    }
    if (form.horaLargada.trim().length < 4) {
      return "Informe a hora da largada.";
    }
    if (!Number.isFinite(Number(form.numeroAtletas)) || Number(form.numeroAtletas) < 1) {
      return "Informe o número de atletas.";
    }
    if (!form.modalidadeTexto.trim() && !form.modalidadeId) {
      return "Informe ou selecione a modalidade.";
    }
    if (form.distanciaProva.trim().length < 1) {
      return "Informe a distância da prova.";
    }
    if (Object.keys(selectedServicos).length < 1) {
      return "Selecione ao menos um serviço.";
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const modalidadeTexto =
      selectedModalidade?.label?.trim() || form.modalidadeTexto.trim();

    const payload: ICreateSolicitacaoOrcamentoAdminPayload = {
      nomeOrganizador: form.nomeOrganizador.trim(),
      email: form.email.trim(),
      telefone: numbersOnly(form.telefone),
      cidade: form.cidade.trim(),
      dataEvento: form.dataEvento,
      numeroAtletas: Number(form.numeroAtletas),
      largadaChegadaMesmoPonto: form.largadaChegadaMesmoPonto,
      modalidadeTexto,
      modalidadeId: form.modalidadeId,
      distanciaProva: form.distanciaProva.trim(),
      horaLargada: form.horaLargada,
      observacoes: form.observacoes.trim() || null,
      obsInterna: form.obsInterna.trim() || null,
      aceiteLgpd: false,
      itens: Object.entries(selectedServicos).map(([servicoId, quantidade]) => ({
        servicoId: Number(servicoId),
        quantidade,
      })),
    };

    setSaving(true);
    setError(null);
    try {
      const created = await criarSolicitacaoOrcamentoAdmin(payload);
      onCreated(created.id, created.codigo);
      onClose();
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Falha ao criar a solicitação."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: modalPaperSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography
              variant="overline"
              sx={{ opacity: 0.75, letterSpacing: 1.2 }}
            >
              Novo orçamento
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              Criar solicitação
            </Typography>
            <Typography
              variant="body2"
              sx={{ opacity: 0.85, mt: 0.5, maxWidth: 560 }}
            >
              Preencha os dados do cliente e do evento. O e-mail só será enviado
              depois, ao clicar em “Enviar proposta”.
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={saving}
            sx={{ color: "inherit" }}
            aria-label="Fechar"
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 0, overflow: "auto" }}>
        <Box p={3}>
          <Stack spacing={2.5}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            <Box sx={sectionCardSx}>
              <SectionTitle
                icon={<PersonOutlineIcon fontSize="small" />}
                title="Contato do cliente"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Nome do organizador"
                    value={form.nomeOrganizador}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      patchForm({ nomeOrganizador: e.target.value })
                    }
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="E-mail"
                    type="email"
                    value={form.email}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      patchForm({ email: e.target.value })
                    }
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Telefone / WhatsApp"
                    value={form.telefone}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      patchForm({ telefone: e.target.value })
                    }
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Cidade do evento"
                    value={form.cidade}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      patchForm({ cidade: e.target.value })
                    }
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={sectionCardSx}>
              <SectionTitle
                icon={<EventOutlinedIcon fontSize="small" />}
                title="Dados do evento"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Data do evento"
                    InputLabelProps={{ shrink: true }}
                    value={form.dataEvento}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      patchForm({ dataEvento: e.target.value })
                    }
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="time"
                    label="Hora da largada"
                    InputLabelProps={{ shrink: true }}
                    value={form.horaLargada}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      patchForm({ horaLargada: e.target.value })
                    }
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Número de atletas"
                    value={form.numeroAtletas}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      patchForm({ numeroAtletas: e.target.value })
                    }
                    inputProps={{ min: 1 }}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={modalidadeOptions}
                    value={selectedModalidade}
                    loading={loadingMeta}
                    onChange={(_e, option) =>
                      patchForm({
                        modalidadeId: option?.id ?? null,
                        modalidadeTexto: option?.label ?? form.modalidadeTexto,
                      })
                    }
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Modalidade"
                        placeholder="Selecione ou digite abaixo"
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Modalidade (texto livre)"
                    value={form.modalidadeTexto}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      patchForm({
                        modalidadeTexto: e.target.value,
                        modalidadeId: null,
                      })
                    }
                    helperText="Use se a modalidade não estiver no catálogo"
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Distância da prova"
                    placeholder="Ex.: 5 km / 10 km"
                    value={form.distanciaProva}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      patchForm({ distanciaProva: e.target.value })
                    }
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.largadaChegadaMesmoPonto}
                        onChange={(e) =>
                          patchForm({
                            largadaChegadaMesmoPonto: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Largada e chegada no mesmo ponto"
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={sectionCardSx}>
              <SectionTitle
                icon={<Inventory2OutlinedIcon fontSize="small" />}
                title="Serviços iniciais"
              />
              {loadingMeta ? (
                <Typography variant="body2" color="text.secondary">
                  Carregando catálogo...
                </Typography>
              ) : servicos.length === 0 ? (
                <Alert severity="warning">
                  Nenhum serviço ativo encontrado. Cadastre serviços antes de
                  criar o orçamento.
                </Alert>
              ) : (
                <Stack spacing={1}>
                  {servicos.map((servico) => {
                    const checked = selectedServicos[servico.id] != null;
                    const quantidade = selectedServicos[servico.id] ?? 1;
                    return (
                      <Box
                        key={servico.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          flexWrap: "wrap",
                          p: 1.25,
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: checked ? ORCAMENTO_ACCENT : "grey.200",
                          bgcolor: checked
                            ? "rgba(249, 96, 48, 0.04)"
                            : "transparent",
                        }}
                      >
                        <FormControlLabel
                          sx={{ flex: 1, minWidth: 220, m: 0 }}
                          control={
                            <Checkbox
                              checked={checked}
                              onChange={(e) =>
                                toggleServico(servico, e.target.checked)
                              }
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {servico.nome}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatMoneyBR(Number(servico.precoUnitario) || 0)}
                                {" / "}
                                {servico.unidadeMedida}
                                {!servico.exibirSite ? " · só interno" : ""}
                              </Typography>
                            </Box>
                          }
                        />
                        {checked && servico.permiteQuantidade ? (
                          <TextField
                            size="small"
                            type="number"
                            label="Qtd"
                            value={String(quantidade)}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                              setQuantidade(servico.id, Number(e.target.value))
                            }
                            inputProps={{ min: 1 }}
                            sx={{ ...fieldSx, width: 100 }}
                          />
                        ) : null}
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>

            <Box sx={sectionCardSx}>
              <SectionTitle
                icon={<NotesOutlinedIcon fontSize="small" />}
                title="Observações"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={4}
                    label="Observações do cliente"
                    value={form.observacoes}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      patchForm({ observacoes: e.target.value })
                    }
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={4}
                    label="Observação interna"
                    value={form.obsInterna}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      patchForm({ obsInterna: e.target.value })
                    }
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "grey.200" }}>
        <MDButton variant="outlined" color="dark" onClick={onClose} disabled={saving}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          onClick={handleSave}
          disabled={saving || loadingMeta}
          sx={{
            background: `linear-gradient(195deg, ${ORCAMENTO_ACCENT}, #e04a1a)`,
          }}
        >
          {saving ? "Criando..." : "Criar e abrir precificação"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
